import cloneDeep from 'lodash/cloneDeep';
import type { ActorRefFrom } from 'xstate';
import { assign, fromPromise, setup } from 'xstate';

import type { Recorder } from '../components/view/audio-recorder/recorder';

import { surveyApi } from './api';

export enum AudioState {
    INITIAL = 'initial',
    INITIALIZING = 'initializing',
    INITIALIZED = 'initialized',
    ERROR_OF_INIT = 'error_of_init',
    STARTING = 'starting',
    STARTED = 'started',
    ERROR_OF_START = 'error_of_start',
    STOPPING = 'stopping',
    STOPPED = 'stopped',
    ERROR_OF_STOP = 'error_of_stop',
}

export type AudioEvents =
    | { type: 'INITIALIZE'; recorder: Recorder }
    | { type: 'START' }
    | { type: 'STOP' }
    | { type: 'SEND' };

export type AudioContext = {
    recorder: Recorder | null;
};

export type AudioType = {
    value: AudioState;
    context: AudioContext;
};

export const initialAudioContext: Readonly<AudioContext> = {
    recorder: null,
};

export const AudioMachineDefinition = setup({
    types: {
        context: {} as AudioContext,
        events: {} as AudioEvents,
    },
    actors: {
        initRecorder: fromPromise<{ type: 'INITIALIZED' }, { recorder: Recorder }>(async ({ input }) => {
            await input.recorder.init();
            return { type: 'INITIALIZED' };
        }),
        startRecorder: fromPromise<{ type: 'STARTED' }, { recorder: Recorder }>(({ input }) => {
            input.recorder.startRecording();
            return Promise.resolve({ type: 'STARTED' });
        }),
        stopRecorder: fromPromise<{ type: 'STOPPED' }, { recorder: Recorder }>(async ({ input }) => {
            try {
                const recordResult = await input.recorder.stopRecording();
                await surveyApi.sendAudio(recordResult);
            } catch (e) {
                // TODO: handle errors
                console.error('Error on sending audio record', JSON.stringify(e));
            }
            return { type: 'STOPPED' };
        }),
    },
}).createMachine({
    initial: AudioState.INITIAL,
    context: cloneDeep(initialAudioContext),
    states: {
        [AudioState.INITIAL]: {
            on: {
                INITIALIZE: {
                    target: AudioState.INITIALIZING,
                    actions: [
                        assign(({ event }) => ({
                            recorder: event.recorder,
                        })),
                    ],
                },
            },
        },
        [AudioState.INITIALIZING]: {
            invoke: {
                src: 'initRecorder',
                input: ({ context }) => ({ recorder: context.recorder }),
                onDone: {
                    target: AudioState.INITIALIZED,
                },
                onError: {
                    target: AudioState.ERROR_OF_INIT,
                },
            },
        },
        [AudioState.INITIALIZED]: {
            on: {
                START: {
                    target: AudioState.STARTING,
                },
            },
        },
        [AudioState.ERROR_OF_INIT]: {},
        [AudioState.STARTING]: {
            invoke: {
                src: 'startRecorder',
                input: ({ context }) => ({ recorder: context.recorder }),
                onDone: {
                    target: AudioState.STARTED,
                },
                onError: {
                    target: AudioState.ERROR_OF_INIT,
                },
            },
        },
        [AudioState.STARTED]: {
            on: {
                STOP: {
                    target: AudioState.STOPPING,
                },
            },
        },
        [AudioState.ERROR_OF_START]: {},
        [AudioState.STOPPING]: {
            invoke: {
                src: 'stopRecorder',
                input: ({ context }) => ({ recorder: context.recorder }),
                onDone: {
                    target: AudioState.STOPPED,
                },
                onError: {
                    target: AudioState.ERROR_OF_STOP,
                },
            },
        },
        [AudioState.STOPPED]: {},
        [AudioState.ERROR_OF_STOP]: {},
    },
});

export type AudioMachineDefinitionRef = ActorRefFrom<typeof AudioMachineDefinition>;
